import { useState } from 'react';

import { BsEnvelope, BsPlus } from 'react-icons/bs';

import Alert from '#globalComponents/Alert/Alert';
import Button from '#globalComponents/Button/Button';
import SectionCard from '#globalComponents/Card/SectionCard';
import EmptyState from '#globalComponents/EmptyState/EmptyState';
import { useError } from '#globalContexts/ErrorContext';

import {
	useCreateEmailAccount,
	useUpdateEmailAccount,
	useDeleteEmailAccount,
} from '../../models/emailAccounts/mutations';
import { useAllEmailAccounts } from '../../models/emailAccounts/queries';

import EmailAccountListItem from './components/EmailAccountListItem';
import EmailAccountModal from './components/EmailAccountModal';

export const SMTPSettings = () => {
	const { data: accounts = [], isLoading, error } = useAllEmailAccounts();
	const createMutation = useCreateEmailAccount();
	const updateMutation = useUpdateEmailAccount();
	const deleteMutation = useDeleteEmailAccount();
	const { showSuccess, showError } = useError();

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedAccount, setSelectedAccount] = useState(null);

	const handleAddAccount = () => {
		setSelectedAccount(null);
		setIsModalOpen(true);
	};

	const handleEditAccount = (account) => {
		setSelectedAccount(account);
		setIsModalOpen(true);
	};

	const handleDeleteAccount = async (accountId) => {
		try {
			await deleteMutation.mutateAsync(accountId);
			showSuccess({
				title: 'Erfolgreich gelöscht',
				message: 'E-Mail Konto wurde erfolgreich gelöscht',
				autoClose: true,
			});
		} catch (error) {
			showError({
				title: 'Fehler beim Löschen',
				error,
				autoClose: false,
			});
		}
	};

	const handleSaveAccount = async (payload) => {
		try {
			if (selectedAccount) {
				await updateMutation.mutateAsync(payload);
			} else {
				await createMutation.mutateAsync(payload);
			}

			showSuccess({
				title: selectedAccount ? 'Erfolgreich aktualisiert' : 'Erfolgreich hinzugefügt',
				message: selectedAccount
					? 'E-Mail Konto wurde erfolgreich aktualisiert'
					: 'E-Mail Konto wurde erfolgreich hinzugefügt',
				autoClose: true,
			});
			setIsModalOpen(false);
			setSelectedAccount(null);
		} catch (error) {
			showError({
				title: 'Fehler beim Speichern',
				error,
				autoClose: false,
			});
		}
	};

	const handleCloseModal = () => {
		setIsModalOpen(false);
		setSelectedAccount(null);
	};

	const renderContent = () => {
		if (isLoading) {
			return (
				<div className="flex items-center justify-center py-12">
					<div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
				</div>
			);
		}
		if (error) {
			return (
				<Alert tone="warning" title="Fehler beim Laden der E-Mail-Konten">
					{error.message}
				</Alert>
			);
		}
		if (accounts.length === 0) {
			return (
				<EmptyState
					icon={BsEnvelope}
					title="Keine E-Mail-Konten vorhanden"
					description="Fügen Sie Ihr erstes E-Mail-Konto hinzu, um mit dem Kampagnenversand zu starten."
					action={
						<Button variant="solid" size="md" onClick={handleAddAccount}>
							Erstes Konto hinzufügen
						</Button>
					}
				/>
			);
		}
		return (
			<div className="space-y-4">
				<div className="space-y-3">
					{accounts.map((account) => (
						<EmailAccountListItem
							key={account.id}
							account={account}
							onClick={handleEditAccount}
							onDelete={handleDeleteAccount}
						/>
					))}
				</div>
				<Alert tone="info" title="Tipp: Mehrere Konten für bessere Zustellbarkeit">
					Nutzen Sie mehrere E-Mail-Konten, um Ihre Kampagnen zu verteilen und die Zustellbarkeit zu
					verbessern. Achten Sie darauf, die Versandlimits Ihrer E-Mail-Provider zu beachten.
				</Alert>
			</div>
		);
	};

	return (
		<>
			<SectionCard
				icon={BsEnvelope}
				title="E-Mail-Konten"
				subtitle="Verwalten Sie Ihre E-Mail-Konten für den Kampagnenversand"
				headerAction={
					<Button variant="solid" size="sm" onClick={handleAddAccount}>
						<BsPlus className="h-4 w-4" />
						Konto hinzufügen
					</Button>
				}
			>
				{renderContent()}
			</SectionCard>

			<EmailAccountModal
				isOpen={isModalOpen}
				onClose={handleCloseModal}
				account={selectedAccount}
				onSave={handleSaveAccount}
			/>
		</>
	);
};
